import {
  IFileVariantResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IFileVariantResponseInstance'
import {FileVariantType} from '@toolify/server/src/models/mongoose/FileVariantModel/enum/FileVariantType'
import {StaticFileVariantKey} from '@toolify/server/src/services/FileVariantService/enum/StaticFileVariantKey'
import {
  IImageToImageFileConversionTypeStrategyInterfaces,
} from '@toolify/server/src/services/FileVariantService/strategies/FileConversionTypeStrategies/ImageToImageFileConversionTypeStrategy/types/IImageToImageFileConversionTypeStrategyInterfaces'
import {
  getImageFileVariantSize,
  IImageFileVariantSize,
} from '@toolify/client/src/modules/ui/functions/getImageFileVariantSize'
import {filterImageFileVariants} from '@toolify/client/src/modules/ui/functions/filterImageFileVariants'

type ImageFileVariant = IFileVariantResponseInstance<IImageToImageFileConversionTypeStrategyInterfaces['payload']>

export function getBestImageFileVariant(
  fileVariants: IFileVariantResponseInstance[],
  elementWidth: number,
  elementHeight: number,
  deviceAspectRatio: number,
  maxAreaDifference?: number,
  options?: {animated?: boolean},
): ImageFileVariant | null {
  const pool = filterImageFileVariants(fileVariants, options?.animated)
  return pickBestImageFileVariant(pool, elementWidth, elementHeight, deviceAspectRatio, maxAreaDifference)
}

export function pickBestImageFileVariant(
  fileVariants: IFileVariantResponseInstance[],
  elementWidth: number,
  elementHeight: number,
  deviceAspectRatio: number,
  maxAreaDifference?: number,
): ImageFileVariant | null {
  // NOTE: `deviceAspectRatio` is actually DPR (device pixel ratio) as passed by `useDevicePixelRatio()`.
  // We want to avoid choosing images that require upscaling (blurry) and prefer the smallest image
  // that can satisfy DPR-scaled dimensions, while still favoring close aspect ratios.
  if(!fileVariants?.length || !elementWidth || !elementHeight || !deviceAspectRatio) {
    return null
  }

  const dpr = Math.max(1, deviceAspectRatio)
  const elementAspectRatio = elementWidth / elementHeight
  const targetWidth = Math.max(1, elementWidth * dpr)
  const targetHeight = Math.max(1, elementHeight * dpr)
  const targetArea = targetWidth * targetHeight

  const sizeByFileVariantMap = new Map<IFileVariantResponseInstance, IImageFileVariantSize>()
  for(const fileVariant of fileVariants) {
    if(fileVariant?.type !== FileVariantType.Image) {
      continue
    }
    const size = getImageFileVariantSize(fileVariant)
    if(size) {
      sizeByFileVariantMap.set(fileVariant, size)
    }
  }
  const allImageFileVariants = fileVariants.filter(fileVariant => sizeByFileVariantMap.has(fileVariant)) as ImageFileVariant[]
  const originalSize = sizeByFileVariantMap.get(
    fileVariants.find(fileVariant => (
      fileVariant?.key === StaticFileVariantKey.CompressedImageWebp
      || fileVariant?.key === StaticFileVariantKey.CompressedImage
      || fileVariant?.key === StaticFileVariantKey.AnimatedImage
    )),
  )
  const notUpscaledImageFileVariants = originalSize
    ? allImageFileVariants.filter(fileVariant => {
      const size = sizeByFileVariantMap.get(fileVariant)
      return size.width <= originalSize.width && size.height <= originalSize.height
    })
    : []
  const imageFileVariants = notUpscaledImageFileVariants.length ? notUpscaledImageFileVariants : allImageFileVariants

  if(!imageFileVariants.length) {
    // TODO: Remove this after migrating all PDF files to generate proper image variants with dimensions
    const fallbackImageVariant = fileVariants.find(v => v.type === FileVariantType.Image)
    if(fallbackImageVariant) {
      return fallbackImageVariant as ImageFileVariant
    }
    return null
  }

  // Optional coarse filter: keep variants within a relative area ratio of the target.
  // If the filter removes everything, we fall back to all variants.
  let candidates = imageFileVariants
  if(maxAreaDifference) {
    const filtered = candidates.filter(variant => {
      const size = sizeByFileVariantMap.get(variant)
      const variantArea = size.width * size.height
      return Math.max(variantArea, targetArea) / Math.min(variantArea, targetArea) <= maxAreaDifference
    })
    if(filtered.length) {
      candidates = filtered
    }
  }

  const coverScale = (variant: ImageFileVariant) => {
    // Scale factor needed for `object-fit: cover` to fill the DPR-scaled box.
    const size = sizeByFileVariantMap.get(variant)
    return Math.max(targetWidth / size.width, targetHeight / size.height)
  }

  const scoreVariant = (variant: ImageFileVariant) => {
    const size = sizeByFileVariantMap.get(variant)
    const w = size.width
    const h = size.height
    const area = w * h
    const aspectRatio = w / h

    const scale = coverScale(variant)
    const needsUpscale = scale > 1

    // Strongly discourage upscaling (blurry). Downscaling is OK but we still want "not too large".
    const upscalePenalty = needsUpscale ? (scale - 1) * 1000 : 0
    const downscalePenalty = !needsUpscale ? Math.log2(1 / Math.max(scale, 1e-6)) : 0

    // Prefer closer aspect ratios to reduce crop in `object-fit: cover`, but don't hard-exclude.
    const aspectPenalty = Math.abs(aspectRatio - elementAspectRatio) * 10

    // Small nudge toward “closest to target” once upscaling is handled.
    const areaPenalty = Math.abs(area - targetArea) / targetArea

    return upscalePenalty + downscalePenalty + aspectPenalty + areaPenalty
  }

  // Prefer variants that can cover without upscaling.
  const noUpscale = candidates.filter(v => coverScale(v) <= 1)
  const pool = noUpscale.length ? noUpscale : candidates

  return pool
    .slice()
    .sort((a, b) => scoreVariant(a) - scoreVariant(b))?.[0] || null
}
